import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { StaffProfessionResponse } from '../../api/generated';
import type { RootState } from '../index';

interface StaffCompetencesState {
  competencesById: Record<string, StaffProfessionResponse>;
  loading: boolean;
  error: string | null;
  // Distinguishes "never fetched" from "fetched, and there are none", so an
  // account with no competences does not refetch on every mount. Set on
  // success only: after a failure the next consumer to mount retries, which is
  // what keeps a transient error from stranding every reader on an empty list
  // until a browser reload. See useStaffCompetences.
  hasFetched: boolean;
}

const initialState: StaffCompetencesState = {
  competencesById: {},
  loading: false,
  error: null,
  hasFetched: false
};

const staffCompetencesSlice = createSlice({
  name: 'staffCompetences',
  initialState,
  reducers: {
    // Dispatched with `true` before the request goes out. It doubles as the
    // in-flight guard that stops several consumers mounting in the same commit
    // from each firing their own fetch — see useStaffCompetences.
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setCompetences: (state, action: PayloadAction<StaffProfessionResponse[]>) => {
      const competencesById: Record<string, StaffProfessionResponse> = {};
      for (const c of action.payload) {
        competencesById[c.id] = c;
      }
      state.competencesById = competencesById;
      state.loading = false;
      state.error = null;
      state.hasFetched = true;
    },
    upsertCompetence: (state, action: PayloadAction<StaffProfessionResponse>) => {
      state.competencesById[action.payload.id] = action.payload;
    },
    removeCompetence: (state, action: PayloadAction<string>) => {
      delete state.competencesById[action.payload];
    },
    clearCompetences: (state) => {
      state.competencesById = {};
      state.loading = false;
      state.error = null;
      state.hasFetched = false;
    }
  }
});

export const {
  setLoading,
  setError,
  setCompetences,
  upsertCompetence,
  removeCompetence,
  clearCompetences
} = staffCompetencesSlice.actions;

// Memoized: Object.values creates a new array per call, and an unmemoized
// selector would re-render every subscriber (incl. the Gantt week header,
// which recomputes all week KPIs) on every store action.
export const selectStaffCompetences = createSelector(
  (state: RootState) => state.staffCompetencesSlice.competencesById,
  (competencesById): StaffProfessionResponse[] => Object.values(competencesById)
);

export const selectStaffCompetenceById =
  (id: string | undefined) =>
  (state: RootState): StaffProfessionResponse | undefined =>
    id ? state.staffCompetencesSlice.competencesById[id] : undefined;

export const selectStaffCompetencesLoading = (state: RootState): boolean =>
  state.staffCompetencesSlice.loading;

export const selectStaffCompetencesError = (state: RootState): string | null =>
  state.staffCompetencesSlice.error;

export const selectHasFetchedStaffCompetences = (state: RootState): boolean =>
  state.staffCompetencesSlice.hasFetched;

export default staffCompetencesSlice.reducer;
